"use client"

import { useEffect, useState } from "react"

type Item = { id: string; text: string }

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

/**
 * "On this page": the article's h2s, read from the page once it renders.
 * Each heading gets an id if it has none, and the one in view is marked.
 */
export function ArticleToc() {
  const [items, setItems] = useState<Item[]>([])
  const [active, setActive] = useState<string>("")

  useEffect(() => {
    const heads = Array.from(document.querySelectorAll<HTMLHeadingElement>("[data-article] h2"))
    const seen = new Set<string>()
    const list = heads.map((h) => {
      if (!h.id) {
        let id = slug(h.textContent ?? "") || "section"
        while (seen.has(id) || document.getElementById(id)) id += "-2"
        h.id = id
      }
      seen.add(h.id)
      h.classList.add("scroll-mt-20")
      return { id: h.id, text: h.textContent ?? "" }
    })
    setItems(list)
    if (list.length) setActive(list[0].id)
    if (!heads.length) return

    // The active heading is the last one above the top third of the viewport.
    const pick = () => {
      const line = window.innerHeight * 0.3
      let current = heads[0].id
      for (const h of heads) {
        if (h.getBoundingClientRect().top <= line) current = h.id
      }
      setActive(current)
    }
    pick()
    window.addEventListener("scroll", pick, { passive: true })
    return () => window.removeEventListener("scroll", pick)
  }, [])

  if (items.length < 2) return null

  return (
    <nav className="flex flex-col gap-0.5 border-l border-[#E6E8EC] text-sm" aria-label="On this page">
      <span className="pb-2 pl-3.5 font-mono text-[11px] tracking-[0.1em] text-[#5B6270]">ON THIS PAGE</span>
      {items.map((it) => {
        const on = it.id === active
        return (
          <a
            key={it.id}
            href={`#${it.id}`}
            aria-current={on ? "location" : undefined}
            className={`-ml-px border-l-2 py-1.5 pl-3.5 no-underline ${on ? "border-[#E0312A] text-[#161B26]" : "border-transparent text-[#5B6270] hover:text-[#161B26]"}`}
          >
            {it.text}
          </a>
        )
      })}
    </nav>
  )
}
